import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Ruler, Truck, RefreshCw, Wallet, MapPin, MessageCircle, Check, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, errMsg } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { useShop } from "@/context/ShopContext";
import { inr, waLink, OCCASION_LABEL, COMBO } from "@/lib/content";
import { Stars, ComboTag } from "@/components/site/ProductCard";
import { SizeTable, FitNotes } from "@/components/site/SizeGuide";
import ReviewsSection from "@/components/site/ReviewsSection";
import { ProductGrid } from "@/components/home/Sections";

const Gallery = ({ p }) => {
  const [idx, setIdx] = useState(0);
  const [origin, setOrigin] = useState(null);
  const [full, setFull] = useState(false);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <div className="flex flex-col-reverse gap-3 sm:flex-row lg:sticky lg:top-24">
      <div className="no-scrollbar flex gap-3 overflow-x-auto sm:flex-col">
        {p.images.map((src, i) => (
          <button key={i} data-testid={`pdp-thumb-${i}`} onClick={() => setIdx(i)} className={`w-16 shrink-0 overflow-hidden rounded-xl border-2 sm:w-20 ${i === idx ? "border-[#701A24]" : "border-transparent opacity-70"}`}>
            <img src={src} alt={`${p.name} view ${i + 1}`} className="aspect-[3/4] w-full object-cover object-top" />
          </button>
        ))}
      </div>
      <div data-testid="pdp-main-image" onMouseMove={move} onMouseLeave={() => setOrigin(null)} onClick={() => setFull(true)} className="relative flex-1 cursor-zoom-in overflow-hidden rounded-3xl bg-[#F4EFEA]">
        <img src={p.images[idx]} alt={p.alt} className="aspect-[3/4] w-full object-cover object-top transition-transform duration-200" style={origin ? { transform: "scale(2)", transformOrigin: origin } : undefined} />
        <span className="absolute bottom-4 right-4 flex items-center gap-1 rounded-full bg-white/85 px-3 py-1.5 text-xs backdrop-blur"><ZoomIn className="h-3.5 w-3.5" /> Hover or tap to zoom</span>
      </div>
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-3xl bg-[#FAF7F2] p-2">
          <DialogTitle className="sr-only">{p.name}</DialogTitle>
          <img src={p.images[idx].replace("w=900", "w=1600")} alt={p.alt} className="max-h-[85vh] w-full rounded-2xl object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PincodeChecker = () => {
  const [pin, setPin] = useState(localStorage.getItem("kt_pin") || "");
  const [res, setRes] = useState(null);
  const check = async (e) => {
    e.preventDefault();
    try {
      const r = await api.get(`/pincode/${pin}`);
      setRes(r.data);
      localStorage.setItem("kt_pin", pin);
    } catch (err) {
      setRes(null);
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="rounded-2xl border border-[#E7E2DB] bg-white p-5">
      <p className="flex items-center gap-2 text-sm font-semibold"><MapPin className="h-4 w-4 text-[#B85D43]" /> Check delivery</p>
      <form onSubmit={check} className="mt-3 flex gap-2">
        <input data-testid="pincode-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="Enter 6-digit pincode" className="flex-1 rounded-full border border-[#E7E2DB] px-4 py-2.5 text-sm outline-none focus:border-[#B85D43]" />
        <button data-testid="pincode-check-button" className="rounded-full bg-[#1C1917] px-5 text-sm font-semibold text-white">Check</button>
      </form>
      {res && (
        <ul data-testid="pincode-result" className="mt-4 space-y-2 text-sm">
          <li className="flex gap-2"><Truck className="h-4 w-4 text-[#1B4332]" /> Estimated delivery: <b>{res.estimate}</b></li>
          <li className="flex gap-2"><Wallet className="h-4 w-4 text-[#1B4332]" /> Prepaid orders only (COD temporarily paused)</li>
          <li className="flex gap-2"><RefreshCw className="h-4 w-4 text-[#1B4332]" /> Easy 7-day size exchange</li>
        </ul>
      )}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addToCart, toggleWish, isWished } = useShop();
  const [data, setData] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [guide, setGuide] = useState(false);
  const [soldOut, setSoldOut] = useState(null);
  const requestRestock = async () => {
    try {
      await api.post("/restock-requests", { slug: p.slug, size: soldOut });
      toast.success(`Restock request noted for size ${soldOut}`);
    } catch (e) { toast.error(errMsg(e)); }
    setSoldOut(null);
  };
  const p = data?.product;
  useSeo(p?.name, p ? `${p.name} – ${p.fabric} ${p.category_label.toLowerCase()} for women at ${inr(p.price)}. ${p.description.slice(0, 90)}` : null);

  useEffect(() => {
    setData(null); setSize("");
    api.get(`/products/${slug}`).then((r) => { setData(r.data); setColor(r.data.product.colors[0].name); });
  }, [slug]);

  if (!p) return <div className="kt-container grid gap-10 py-12 lg:grid-cols-2"><div className="aspect-[3/4] animate-pulse rounded-3xl bg-[#EAE0D5]" /><div className="h-96 animate-pulse rounded-3xl bg-[#EAE0D5]" /></div>;

  const stock = size ? p.stock[size] : null;
  const add = (buy) => {
    if (!size) { toast.error("Please select a size"); document.getElementById("size-selector")?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    addToCart(p, size, color, 1, !buy);
    if (buy) nav("/checkout");
  };

  return (
    <div data-testid="product-detail-page">
      <div className="kt-container py-8 sm:py-12">
        <nav className="mb-6 text-xs text-[#78716C]"><Link to="/">Home</Link> / <Link to={`/collections/${p.category}`}>{p.category_label}</Link> / <span className="text-[#1C1917]">{p.name}</span></nav>
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7"><Gallery p={p} /></div>
          <div className="lg:col-span-5">
            {p.label && <span className="eyebrow">{p.label}</span>}
            <h1 data-testid="pdp-title" className="mt-2 font-serif-display text-4xl font-semibold leading-tight sm:text-5xl">{p.name}</h1>
            {p.review_count > 0 && <a href="#reviews" className="mt-3 flex items-center gap-2 text-sm text-[#57534E]"><Stars value={p.rating} /> {p.rating} · {p.review_count} reviews</a>}
            <div className="mt-5 flex items-baseline gap-3">
              <span data-testid="pdp-price" className="text-3xl font-semibold">{inr(p.price)}</span>
              <span className="text-base text-[#A8A29E] line-through">MRP {inr(p.mrp)}</span>
              <span className="rounded-full bg-[#1B4332] px-2.5 py-1 text-xs font-semibold text-white">{p.discount_pct}% OFF</span>
            </div>
            <p className="mt-1 text-xs text-[#78716C]">Inclusive of all taxes · Extra 10% off on prepaid orders</p>
            {p.price === COMBO.item && <ComboTag big />}

            <div className="mt-8">
              <p className="text-sm font-semibold">Colour: <span className="font-normal text-[#57534E]">{color}</span></p>
              <div className="mt-3 flex gap-3">
                {p.colors.map((c) => (
                  <button key={c.name} data-testid={`pdp-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setColor(c.name)} aria-label={c.name} style={{ background: c.hex }} className={`h-9 w-9 rounded-full border border-black/10 ring-offset-2 transition ${color === c.name ? "ring-2 ring-[#701A24]" : ""}`} />
                ))}
              </div>
            </div>

            <div id="size-selector" className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Size {size && <span className="font-normal text-[#57534E]">: {size}</span>}</p>
                <button data-testid="open-size-guide-button" onClick={() => setGuide(true)} className="flex items-center gap-1.5 text-sm font-medium text-[#B85D43] underline-offset-4 hover:underline"><Ruler className="h-4 w-4" /> Size Guide</button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
                {p.sizes.map((s) => {
                  const out = p.stock[s] < 1;
                  return (
                    <button key={s} data-testid={`pdp-size-${s}`} aria-label={out ? `${s} – Size Not Available` : `Size ${s}`} onClick={() => (out ? setSoldOut(s) : setSize(s))} className={`relative flex flex-col items-center justify-center rounded-xl border py-2.5 text-sm font-medium transition-colors ${out ? "cursor-not-allowed border-dashed border-[#D6CFC6] bg-[#F4EFEA] text-[#A8A29E]" : size === s ? "border-[#701A24] bg-[#701A24] text-white" : "border-[#E7E2DB] bg-white hover:border-[#701A24]"}`}>
                      <span className={out ? "line-through" : ""}>{s}</span>
                      {out && <span className="mt-0.5 text-[9px] font-normal leading-tight">Size Not Available</span>}
                    </button>
                  );
                })}
              </div>
              {stock !== null && stock <= 3 && stock > 0 && <p data-testid="pdp-low-stock" className="mt-2 text-sm font-medium text-[#B85D43]">Only {stock} left in size {size}</p>}
            </div>

            <div className="mt-8 hidden gap-3 sm:flex">
              <button data-testid="pdp-add-to-cart-button" onClick={() => add(false)} className="btn-outline flex-1">Add to Cart</button>
              <button data-testid="pdp-buy-now-button" onClick={() => add(true)} className="btn-primary flex-1">Buy Now</button>
              <button data-testid="pdp-wishlist-button" onClick={() => toggleWish(p)} aria-label="Wishlist" className="rounded-full border border-[#E7E2DB] p-3.5"><Heart className={`h-5 w-5 ${isWished(p.slug) ? "fill-[#B85D43] text-[#B85D43]" : ""}`} /></button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[11px] text-[#57534E]">
              {[[Truck, "Free shipping"], [Wallet, "Secure UPI payment"], [RefreshCw, "7-day exchange"]].map(([I, t]) => <div key={t} className="rounded-2xl bg-[#F4EFEA] px-2 py-3"><I className="mx-auto mb-1 h-4 w-4 text-[#701A24]" />{t}</div>)}
            </div>
            <div className="mt-6"><PincodeChecker /></div>

            <Accordion type="multiple" defaultValue={["desc"]} className="mt-6">
              <AccordionItem value="desc"><AccordionTrigger className="text-sm font-semibold">Description</AccordionTrigger><AccordionContent className="text-sm leading-relaxed text-[#57534E]">{p.description}<p className="mt-2">Occasion: {p.occasions.map((o) => OCCASION_LABEL[o]).join(", ")}</p></AccordionContent></AccordionItem>
              <AccordionItem value="fabric"><AccordionTrigger className="text-sm font-semibold">Fabric & Care</AccordionTrigger><AccordionContent className="text-sm text-[#57534E]"><p className="mb-2">Fabric: <b>{p.fabric}</b></p><ul className="space-y-1">{p.care.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 text-[#1B4332]" />{c}</li>)}</ul></AccordionContent></AccordionItem>
              <AccordionItem value="fit"><AccordionTrigger className="text-sm font-semibold">Fit & Length</AccordionTrigger><AccordionContent className="text-sm text-[#57534E]"><p>{p.fit}</p><p className="mt-2">{p.length}</p></AccordionContent></AccordionItem>
              <AccordionItem value="exchange"><AccordionTrigger className="text-sm font-semibold">Easy Exchange & Returns</AccordionTrigger><AccordionContent className="text-sm text-[#57534E]">Size not right? Request a free size exchange within 7 days of delivery. Items must be unworn with tags intact. We pick up from your doorstep.</AccordionContent></AccordionItem>
            </Accordion>
            <a data-testid="pdp-whatsapp-button" href={waLink(`Hi Kurtaani, I have a question about ${p.name}.`)} target="_blank" rel="noreferrer" className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#1B4332]"><MessageCircle className="h-4 w-4" /> Questions about size or fabric? WhatsApp us</a>
          </div>
        </div>
      </div>

      <div id="reviews" className="bg-[#F4EFEA]"><ReviewsSection product={p.slug} title="Customer Reviews" /></div>

      {data.complete_the_look.length > 0 && (
        <section data-testid="complete-the-look" className="kt-container py-16 sm:py-20">
          <p className="eyebrow">Style it with</p>
          <h2 className="mb-10 mt-3 font-serif-display text-4xl font-semibold">Complete the Look</h2>
          <ProductGrid products={data.complete_the_look} testid="complete-the-look-grid" />
        </section>
      )}

      <div data-testid="mobile-sticky-buy-bar" className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-[#E7E2DB] bg-[#FAF7F2]/95 p-3 backdrop-blur-md sm:hidden">
        <button data-testid="mobile-add-to-cart-button" onClick={() => add(false)} className="btn-outline flex-1 px-3">Add to Cart</button>
        <button data-testid="mobile-buy-now-button" onClick={() => add(true)} className="btn-primary flex-1 px-3">Buy Now · {inr(p.price)}</button>
      </div>

      <Dialog open={!!soldOut} onOpenChange={(o) => !o && setSoldOut(null)}>
        <DialogContent data-testid="sold-out-dialog" className="max-w-md rounded-3xl bg-[#FAF7F2]">
          <DialogTitle className="font-serif-display text-2xl text-[#701A24]">Size {soldOut} sold out</DialogTitle>
          <p className="text-sm leading-relaxed text-[#44403C]">This size is currently sold out due to high demand in Dandiya Season! Please select another size or request a restock.</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="request-restock-button" onClick={requestRestock} className="btn-primary px-5 py-2.5">Request Restock</button>
            <button data-testid="choose-another-size-button" onClick={() => setSoldOut(null)} className="btn-outline px-5 py-2.5">Choose Another Size</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={guide} onOpenChange={setGuide}>
        <DialogContent data-testid="size-guide-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl bg-[#FAF7F2]">
          <DialogTitle className="font-serif-display text-3xl text-[#701A24]">Size Guide</DialogTitle>
          <SizeTable />
          <p className="text-sm text-[#57534E]"><b>This style:</b> {p.fit}</p>
          <FitNotes />
          <a href={waLink(`Hi Kurtaani, which size should I pick for ${p.name}?`)} target="_blank" rel="noreferrer" className="inline-flex w-fit items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white"><MessageCircle className="h-4 w-4" /> Not sure? WhatsApp us</a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
